import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScoreBadge, currency } from "@/components/leadUtils";
import { MapPin, Clock, DollarSign, Lock, ShieldCheck, Home, Mail, Phone } from "lucide-react";

const roleLabel = { buyer: "Wants to Buy", seller: "Wants to Sell", both: "Buy & Sell" };

export const LeadCard = ({ lead, onClaim, claiming, revealed }) => {
  return (
    <Card
      data-testid={`lead-card-${lead.id}`}
      className="group relative bg-slate-900/80 border-slate-800 hover:border-emerald-500/50 transition-all duration-300 rounded-xl p-6 flex flex-col justify-between shadow-lg hover:shadow-emerald-500/10"
    >
      <div>
        <div className="flex items-start justify-between gap-3 mb-4">
          <ScoreBadge intent={lead.intent} score={lead.score} leadId={lead.id} />
          <Badge className="gap-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-[10px] font-mono uppercase tracking-wider">
            <ShieldCheck className="h-3 w-3" /> Exclusive
          </Badge>
        </div>

        <div className="flex items-center gap-2 mb-1">
          <Home className="h-4 w-4 text-emerald-400" />
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-400 font-semibold">
            {roleLabel[lead.role] || lead.role}
          </span>
        </div>
        <h3 className="font-heading text-xl font-bold text-white mb-3">{lead.name}</h3>

        <div className="space-y-2 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-slate-500" />
            {lead.city ? `${lead.city}, ` : ""}
            {lead.state} · {lead.zip_code}
          </div>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-slate-500" /> Timeline: <span className="text-slate-200">{lead.timeline}</span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-slate-500" /> Budget:{" "}
            <span className="text-slate-200">
              {currency(lead.budget_min)} – {currency(lead.budget_max)}
            </span>
          </div>
        </div>

        {revealed ? (
          <div className="mt-4 pt-4 border-t border-slate-800 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-emerald-400" data-testid={`lead-contact-email-${lead.id}`}>
              <Mail className="h-4 w-4" /> {lead.email}
            </div>
            <div className="flex items-center gap-2 text-emerald-400" data-testid={`lead-contact-phone-${lead.id}`}>
              <Phone className="h-4 w-4" /> {lead.phone}
            </div>
            {lead.notes && <p className="text-slate-400 italic pt-1">"{lead.notes}"</p>}
          </div>
        ) : (
          <div className="mt-4 pt-4 border-t border-slate-800 flex items-center gap-2 text-slate-500 text-sm">
            <Lock className="h-4 w-4" /> Contact details unlock on claim
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <div className="text-xs font-mono text-slate-500">
          Est. commission
          <div className="text-emerald-400 text-base font-semibold">{currency(lead.est_commission)}</div>
        </div>
        {!revealed && onClaim && (
          <Button
            data-testid={`claim-lead-btn-${lead.id}`}
            onClick={() => onClaim(lead)}
            disabled={claiming}
            className="bg-emerald-400 text-slate-950 hover:bg-emerald-300 font-semibold rounded-full px-6"
          >
            {claiming ? "Claiming…" : "Claim Lead"}
          </Button>
        )}
        {revealed && (
          <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Yours</Badge>
        )}
      </div>
    </Card>
  );
};
